import { Fragment, FunctionComponent, useState } from 'react';
import {
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    Drawer,
    Fade,
    Stack,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material';
import { CardListRow } from '@/components/Card/CardListRow';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@heroicons/react/24/solid/PencilIcon';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import { LoadingContainer } from '@/components/Layout/Container';
import { useDeleteUserMutation, useGetUsersQuery } from '@/api/v2/user';
import { useAuthentication } from '@/hooks/useAuthentication';
import { createPlainCreateUser, User } from '@/models/user';
import { UserDrawerContent } from '@/pageContents/userManagement/UserDrawerContent';
import { useNotification } from '@starjack/features/notifications';

export const UserManagementContent: FunctionComponent = () => {
    const theme = useTheme();
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: users } = useGetUsersQuery({ liftOperatorId });
    const [deleteUser] = useDeleteUserMutation();
    const [drawerConfig, setDrawerConfig] = useState<{
        isOpen: boolean;
        user: User;
    }>(null);
    const { addNotification } = useNotification();
    const [deleteLoadingId, setDeleteLoadingId] = useState<string>('');

    const onDeleteUser = (userId: string) => {
        setDeleteLoadingId(userId);
        deleteUser({ liftOperatorId, userId })
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Benutzer erfolgreich gelöscht',
                    type: 'success',
                });
            })
            .catch(() => {
                addNotification({
                    message:
                        'Fehler beim Löschen. Versuchen Sie es später noch einmal',
                    type: 'error',
                });
            })
            .finally(() => {
                setDeleteLoadingId(null);
            });
    };

    const onCloseDrawer = () => {
        setDrawerConfig({
            isOpen: false,
            user: null,
        });
    };

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Benutzerverwaltung
            </Typography>
            <Stack spacing={theme.page.verticalSpacing}>
                <Container
                    sx={{
                        mt: 3,
                        ml: 'unset',
                    }}
                    maxWidth='lg'
                >
                    <Card>
                        <CardContent
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant='h6'>
                                Angelgte Benutzer
                            </Typography>

                            <Button
                                onClick={() => {
                                    setDrawerConfig({
                                        isOpen: true,
                                        user: null,
                                    });
                                }}
                                sx={{
                                    ml: 'auto',
                                }}
                                variant='contained'
                            >
                                + Neuen Benutzer anlegen
                            </Button>
                        </CardContent>
                        <Divider
                            sx={{
                                width: '100%',
                            }}
                        />

                        {users?.map((user, index) => (
                            <Fragment key={index}>
                                <CardListRow
                                    sx={{
                                        position: 'relative',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography>{user.username}</Typography>

                                    <Stack
                                        sx={{
                                            ml: 'auto',
                                            alignItems: 'center',
                                        }}
                                        direction='row'
                                        spacing={2}
                                    >
                                        <IconButton
                                            onClick={() => {
                                                setDrawerConfig({
                                                    isOpen: true,
                                                    user,
                                                });
                                            }}
                                        >
                                            <SvgIcon>
                                                <EditIcon />
                                            </SvgIcon>
                                        </IconButton>

                                        <IconButton>
                                            <SvgIcon>
                                                <DeleteIcon
                                                    onClick={() =>
                                                        onDeleteUser(user.id)
                                                    }
                                                />
                                            </SvgIcon>
                                        </IconButton>
                                    </Stack>
                                    <Fade in={deleteLoadingId === user.id}>
                                        <div>
                                            <LoadingContainer cover />
                                        </div>
                                    </Fade>
                                </CardListRow>

                                {index < users.length - 1 && (
                                    <Divider
                                        sx={{
                                            width: '100%',
                                        }}
                                    />
                                )}
                            </Fragment>
                        ))}
                    </Card>

                    <Drawer
                        anchor='right'
                        open={drawerConfig?.isOpen}
                        onClose={onCloseDrawer}
                    >
                        <UserDrawerContent
                            user={
                                drawerConfig?.user ||
                                createPlainCreateUser(liftOperatorId)
                            }
                            onClose={onCloseDrawer}
                        />
                    </Drawer>
                </Container>
            </Stack>
        </>
    );
};
